"use client";

import React, { useState } from "react";
import styles from "./SortModal.module.scss";
import Button from "@components/atoms/Button";
import RangeSlider from "@components/atoms/RangeSlider";

type SortModalProps = {
  options?: string[];
  defaultSelected?: string[];
  onApply?: (selected: string[]) => void;
  withClear?: boolean;
  multiSelect?: boolean;
  withPriceRange?: boolean;
  maxPrice?: number;
  isApplying?: boolean;
};

export default function SortModal({
  options,
  defaultSelected = [],
  onApply,
  withClear = false,
  multiSelect = false,
  withPriceRange = false,
  maxPrice = 0,
  isApplying = false,
}: SortModalProps) {
  const [selectedOptions, setSelectedOptions] =
    useState<string[]>(defaultSelected);
  const validOptions = options?.filter((option) => option?.trim()) || [];

  const toggleOption = (option: string) => {
    if (multiSelect) {
      setSelectedOptions((prev) =>
        prev.includes(option)
          ? prev.filter((item) => item !== option)
          : [...prev, option]
      );
    } else {
      setSelectedOptions([option]);
    }
  };

  const handleClear = () => {
    setSelectedOptions([]);
    onApply?.([]);
  };

  const handleApply = () => {
    onApply?.(selectedOptions);
  };

  const minValue =
    withPriceRange && selectedOptions[0]?.includes("-")
      ? Number(selectedOptions[0].split("-")[0])
      : 0;
  const maxValue =
    withPriceRange && selectedOptions[0]?.includes("-")
      ? Number(selectedOptions[0].split("-")[1])
      : maxPrice;

  return (
    <div className={styles.sortModal}>
      {validOptions.length > 0 &&
        validOptions.map((option) => {
          const isActive = selectedOptions.includes(option);
          return (
            <div
              key={option}
              className={`${styles.sortItem} ${
                isActive ? styles.sortItemActive : ""
              }`}
              onClick={() => toggleOption(option)}
            >
              {option}
              {multiSelect && (
                <input
                  type="checkbox"
                  readOnly
                  checked={isActive}
                  className={styles.checkbox}
                />
              )}
            </div>
          );
        })}
      {withPriceRange && (
        <div className={styles.priceRangeSlider}>
          <RangeSlider
            minValue={minValue}
            maxValue={maxValue}
            min={0}
            max={maxPrice}
            step={100000}
            onChange={(min, max) => toggleOption(`${min}-${max}`)}
          />
        </div>
      )}

      <div
        className={styles.stickyButtonWrapper + " " + styles.buttonContainer}
      >
        {withClear && (
          <Button
            variant="wishlist_help"
            className={styles.clearButton}
            onClick={handleClear}
          >
            Clear
          </Button>
        )}
        <Button
          variant="wishlist_explore"
          className={styles.applyButton}
          onClick={handleApply}
          isLoading={isApplying}
          disabled={isApplying}
        >
          {isApplying ? "Applying..." : "Apply"}
        </Button>
      </div>
    </div>
  );
}
